import React, { useState, useEffect, useMemo, useCallback } from "react";
import { AgGridTable, Pagination, DateRangePicker } from 'components/common';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import { FaSearch, FaSyncAlt, FaTimes, FaFileSignature, FaFileContract } from "react-icons/fa";
import { FaFileExcel } from "react-icons/fa6";
import { MdFilterAlt, MdPieChart, MdSummarize, MdViewList } from "react-icons/md";

import {
  getContracts,
  getAddendums,
  getCombined,
  exportContracts,
  exportAddendums,
  exportCombined
} from "redux/crm/report/contractSlice";
import { getActiveSalesUnits } from "redux/crm/salesUnitSlice";

import AdvanceFilterDrawer, { getEmptyValues } from "components/common/AdvanceFilterDrawer";
import SalesUnitGroupedDropdown from "components/app/crm/shared/SalesUnitGroupedDropdown";

import { contractColumns, addendumColumns, combinedColumns } from "./contractReportColumns";
import ContractSummary from "./ContractSummary";

// ── Constants ─────────────────────────────────────────────────────────────────

const THIS_MONTH_START = dayjs().startOf("month").format("YYYY-MM-DD");
const THIS_MONTH_END   = dayjs().format("YYYY-MM-DD");
const EMPTY_RANGE      = { startDate: "", endDate: "" };

const TABS = [
  { key: "all",        label: "All",                 Icon: MdViewList,      desc: "Shows both contracts and addendums together." },
  { key: "contracts",  label: "Contracts",           Icon: FaFileSignature, desc: "Shows contracts only." },
  { key: "addendums",  label: "Detail of Addendums", Icon: FaFileContract,  desc: "Shows addendums only." },
  { key: "summary",    label: "Summary",             Icon: MdSummarize,     desc: "Aggregated contract & addendum totals for the selected period." }
];

// Quick Released filter. Default is "Released" (report shows released items first).
// "All" sends 'all' — the backend contract scope defaults to released-only, so an
// explicit 'all' is required to include unreleased rows.
const RELEASED_OPTIONS = [
  { value: "true", label: "Released" },
  { value: "false", label: "Unreleased" },
  { value: "all", label: "All" }
];

// Maps the three quick date pickers to ransack-ish param keys. The signed /
// released keys differ for addendums (they live on the addendum row).
const dateParamKeys = (isAddendum) => ({
  primary:  ["start_date", "end_date"],
  signed:   isAddendum ? ["addendum_signed_date_from", "addendum_signed_date_to"] : ["signed_date_from", "signed_date_to"],
  released: isAddendum ? ["addendum_released_date_from", "addendum_released_date_to"] : ["released_date_from", "released_date_to"]
});

// Advanced drawer fields — contract vs addendum variants.
// Advanced drawer fields — contract vs addendum variants. (Released is a quick
// filter in the bar, not here.)
const buildFilterFields = (isAddendum) =>
  isAddendum
    ? [
        { key: "addendum_number",  label: "Addendum number", type: "text",   paramKey: "addendum_number",    tagName: "Addendum#" },
        { key: "addendum_signed_by", label: "Signed by",     type: "text",   paramKey: "addendum_signed_by", tagName: "Signed by" },
        { key: "created_date",     label: "Created date",    type: "date_range_picker", paramKeys: ["created_date_from", "created_date_to"], tagName: "Created" }
      ]
    : [
        { key: "contract_number",  label: "Contract number", type: "text",   paramKey: "contract_number", tagName: "Contract#" },
        { key: "signed_by",        label: "Signed by",       type: "text",   paramKey: "signed_by",       tagName: "Signed by" },
        { key: "created_date",     label: "Created date",    type: "date_range_picker", paramKeys: ["created_date_from", "created_date_to"], tagName: "Created" }
      ];

// ── Component ─────────────────────────────────────────────────────────────────

const ContractReportPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { allData, addendumData, combinedData, exporting } = useSelector((s) => s.crm_report_contract);
  const { activeSalesUnitsData = [] } = useSelector((s) => s.crm_sales_unit);

  const [view, setView]                 = useState("all");
  const [page, setPage]                 = useState(1);
  const [perPage, setPerPage]           = useState(50);
  const [sort, setSort]                 = useState({ attribute: "display_date", direction: "DESC" });
  const [search, setSearch]             = useState("");
  const [committedSearch, setCommitted] = useState("");
  const [dateRanges, setDateRanges]     = useState({
    primary:  { startDate: THIS_MONTH_START, endDate: THIS_MONTH_END },
    signed:   EMPTY_RANGE,
    released: EMPTY_RANGE
  });
  const [unitIds, setUnitIds]           = useState([]);
  const [releasedFilter, setReleasedFilter] = useState("true"); // Released by default
  const [filterParams, setFilterParams] = useState({});
  const [filterTags, setFilterTags]     = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [showFilter, setShowFilter]     = useState(false);
  const [loading, setLoading]           = useState(false);

  const isCombined = view === "all";
  const isAddendum = view === "addendums";
  const isGridTab = isCombined || view === "contracts" || isAddendum;

  const grid = isCombined ? combinedData : isAddendum ? addendumData : allData;
  const { data = [], totalRows = 0 } = grid || {};
  const columnDefs = isCombined ? combinedColumns : isAddendum ? addendumColumns : contractColumns;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  const filterFields = useMemo(() => buildFilterFields(isAddendum), [isAddendum]);

  useEffect(() => { dispatch(getActiveSalesUnits()); }, [dispatch]);

  // Debounce the quick search input.
  useEffect(() => {
    const t = setTimeout(() => { setCommitted(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // ── Param builders ──────────────────────────────────────────────────────────

  const buildDateParams = useCallback(() => {
    const keys = dateParamKeys(isAddendum);
    const p = {};
    Object.entries(keys).forEach(([k, [from, to]]) => {
      const r = dateRanges[k] || EMPTY_RANGE;
      if (r.startDate) p[from] = r.startDate;
      if (r.endDate)   p[to]   = r.endDate;
    });
    return p;
  }, [dateRanges, isAddendum]);

  const buildCriteria = useCallback(() => {
    const releasedKey = isAddendum ? "addendum_released" : "released";
    return {
      ...(committedSearch ? { q: committedSearch } : {}),
      ...buildDateParams(),
      ...(unitIds.length ? { sales_units: unitIds } : {}),
      ...(releasedFilter ? { [releasedKey]: releasedFilter } : {}),
      ...filterParams
    };
  }, [committedSearch, buildDateParams, unitIds, releasedFilter, isAddendum, filterParams]);

  const buildListParams = useCallback(() => {
    const base = { page, per_page: perPage, ...buildCriteria() };
    // Combined endpoint reads flat sort_by/sort_dir; contract/addendum read a `sort` object.
    return isCombined
      ? { ...base, sort_by: sort.attribute, sort_dir: sort.direction }
      : { ...base, sort };
  }, [page, perPage, sort, buildCriteria, isCombined]);

  // Criteria for the Summary / This-month tabs (units + primary date only).
  const sharedCriteria = useMemo(() => ({
    ...(unitIds.length ? { sales_units: unitIds } : {}),
    start_date: dateRanges.primary.startDate,
    end_date: dateRanges.primary.endDate
  }), [unitIds, dateRanges.primary]);

  // ── Fetch grid tabs ───────────────────────────────────────────────────────

  const fetchGrid = useCallback(() => {
    if (!isGridTab) return;
    setLoading(true);
    const thunk = isCombined ? getCombined : isAddendum ? getAddendums : getContracts;
    dispatch(thunk(buildListParams())).finally(() => setLoading(false));
  }, [isGridTab, isCombined, isAddendum, dispatch, buildListParams]);

  useEffect(() => { fetchGrid(); }, [fetchGrid]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleSwitchTab = (key) => {
    if (key === view) return;
    // Drawer params/tags are tab-specific — reset on switch.
    setFilterParams({});
    setFilterTags([]);
    setFilterValues(getEmptyValues(buildFilterFields(key === "addendums")));
    setPage(1);
    setSort(key === "addendums"
      ? { attribute: "addendum_date", direction: "DESC" }
      : key === "all"
        ? { attribute: "display_date", direction: "DESC" }
        : { attribute: "contract_date", direction: "DESC" });
    setView(key);
  };

  const defaultSortCol = isCombined ? "display_date" : isAddendum ? "addendum_date" : "contract_date";
  const handleSort = useCallback(({ colId, sort: dir }) => {
    setSort(dir ? { attribute: colId, direction: dir.toUpperCase() } : { attribute: defaultSortCol, direction: "DESC" });
    setPage(1);
  }, [defaultSortCol]);

  const handleDateChange = (key) => (range) => {
    setDateRanges((prev) => ({ ...prev, [key]: range }));
    setPage(1);
  };

  const handleUnitChange = (ids) => { setUnitIds(ids); setPage(1); };

  const handleApplyFilter = (params, tags) => {
    setFilterParams(params);
    setFilterTags(tags);
    setShowFilter(false);
    setPage(1);
  };

  const handleResetFilter = () => {
    setFilterParams({});
    setFilterTags([]);
    setFilterValues(getEmptyValues(filterFields));
    setPage(1);
  };

  const removeTag = (tag) => {
    setFilterParams((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== tag.id)));
    setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
    setPage(1);
  };

  const handleReload = () => fetchGrid();

  const handleExport = () => {
    const thunk = isCombined ? exportCombined : isAddendum ? exportAddendums : exportContracts;
    dispatch(thunk(buildListParams()));
  };

  const primaryDateLabel = isCombined ? "Date" : isAddendum ? "Addendum date" : "Contract date";

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: "linear-gradient(to bottom, #0ea5e9, #6366f1)" }} />
          <div className="tw-flex tw-items-center tw-gap-2">
            <FaFileSignature className="tw-text-blue-600" size={20} />
            <div>
              <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Contracts</h1>
              <p className="tw-text-gray-500 tw-text-sm tw-mb-0">List of general contracts and addendums signed for projects.</p>
            </div>
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-gap-2">
          <button type="button" onClick={() => navigate("/dashboard/crm/contract")}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-primary-300 tw-bg-primary-50 tw-text-primary-700 hover:tw-bg-primary-100 tw-text-sm tw-font-medium tw-cursor-pointer">
            <MdPieChart size={14} /> Dashboard
          </button>
          {isGridTab && (
            <>
              <button type="button" onClick={handleReload} disabled={loading}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer disabled:tw-opacity-50">
                <FaSyncAlt size={12} className={loading ? "tw-animate-spin" : ""} /> Reload
              </button>
              <button type="button" onClick={handleExport} disabled={exporting || loading}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-green-300 tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100 tw-text-sm tw-font-medium tw-cursor-pointer disabled:tw-opacity-50">
                <FaFileExcel size={13} /> {exporting ? "Exporting..." : "Export"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">

          {isGridTab && (
            <>
              <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-56 sm:tw-flex-shrink-0">
                <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Project, Q#, J#..."
                  className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
                />
                {search && (
                  <button type="button" onClick={() => setSearch("")}
                    className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-p-0">
                    <FaTimes size={8} />
                  </button>
                )}
              </div>
              <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
            </>
          )}

          {/* Primary date — shown on grid tabs + summary */}
          {(isGridTab || view === "summary") && (
            <DateRangePicker
              label={primaryDateLabel}
              startDate={dateRanges.primary.startDate}
              endDate={dateRanges.primary.endDate}
              onChange={handleDateChange("primary")}
            />
          )}

          {isGridTab && !isCombined && (
            <>
              <DateRangePicker label="Signed date" startDate={dateRanges.signed.startDate} endDate={dateRanges.signed.endDate} onChange={handleDateChange("signed")} />
              <DateRangePicker label="Released date" startDate={dateRanges.released.startDate} endDate={dateRanges.released.endDate} onChange={handleDateChange("released")} />
            </>
          )}

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          <SalesUnitGroupedDropdown
            units={activeSalesUnitsData}
            selectedIds={unitIds}
            onChange={handleUnitChange}
            placeholder="All Units"
            width={220}
          />

          {isGridTab && (
            <>
              <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
              <select
                value={releasedFilter}
                onChange={(e) => { setReleasedFilter(e.target.value); setPage(1); }}
                title="Released status"
                className="tw-px-2.5 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded tw-bg-slate-50 hover:tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-text-slate-700 tw-cursor-pointer tw-flex-shrink-0"
              >
                {RELEASED_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </>
          )}

          {isGridTab && (
            <>
              <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
              <button type="button" onClick={() => setShowFilter(true)}
                className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
                  filterTags.length > 0
                    ? "tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700"
                    : "tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50"
                }`}>
                <MdFilterAlt size={15} /> More Filters
                {filterTags.length > 0 && (
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">
                    {filterTags.length}
                  </span>
                )}
              </button>
            </>
          )}

          {isGridTab && !loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
              {totalRows.toLocaleString()} records
            </span>
          )}
        </div>

        {/* Active filter tags */}
        {filterTags.length > 0 && isGridTab && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
            {filterTags.map((tag) => (
              <span key={tag.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm">
                <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">{Array.isArray(tag.value) ? tag.value.join(" – ") : String(tag.value)}</span>
                <button type="button" onClick={() => removeTag(tag)} className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer">
                  <FaTimes size={8} />
                </button>
              </span>
            ))}
            <button type="button" onClick={handleResetFilter} className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-xs tw-text-red-500 hover:tw-bg-red-500 hover:tw-text-white tw-border tw-border-red-200 hover:tw-border-red-500 tw-bg-transparent tw-cursor-pointer tw-font-medium">
              <FaTimes size={8} /> Clear all
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tw-flex tw-items-center tw-gap-1 tw-border-b tw-border-slate-200">
        {TABS.map(({ key, label, Icon }) => (
          <button key={key} type="button" onClick={() => handleSwitchTab(key)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-border-0 tw-border-b-2 tw-bg-transparent tw-cursor-pointer tw-transition-colors -tw-mb-px ${
              view === key ? "tw-border-primary-600 tw-text-primary-700" : "tw-border-transparent tw-text-slate-500 hover:tw-text-slate-700"
            }`}
            style={{ borderBottomStyle: "solid" }}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {/* Active tab description */}
      <p className="tw-text-xs tw-text-slate-500 tw-mb-0 -tw-mt-1 tw-italic">
        {TABS.find((t) => t.key === view)?.desc}
      </p>

      {/* Content */}
      {isGridTab && (
        <>
          <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
            <AgGridTable
              rowData={data}
              columnDefs={columnDefs}
              loading={loading}
              pagination={false}
              filter={false}
              height="max(320px, calc(100dvh - 360px))"
              onSortChanged={handleSort}
              enableCellTextSelection
            />
          </div>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalResults={totalRows}
            perPage={perPage}
            pageSizeOptions={[20, 50, 100, 200]}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
            disabled={loading}
          />
        </>
      )}

      {view === "summary" && <ContractSummary criteria={sharedCriteria} />}

      <AdvanceFilterDrawer
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={handleApplyFilter}
        onReset={handleResetFilter}
        fields={filterFields}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        title="Apply filters to contracts"
        description="Establish the contract's filtering criteria"
      />
    </div>
  );
};

export default ContractReportPage;
